/**
 * Parse an inquiry's `services` array into a normalized fuel demand.
 *
 * `aircraft_schedules.services_requested` (and the demo's synthetic equivalent)
 * has accumulated several historical shapes; this mirrors the backend
 * `SimulatorGenerateFuelTickets.php` normalization so the visual fuel-truck
 * simulation reads the same signal the real fueling does:
 *
 *  - Masters generator : { type: 'fuel', gallons, fuel: 'jet_a' | 'avgas' }
 *  - Sim presets / DTO : { type|serviceType: 'fuel_jeta' | 'fuel_100ll', quantity }
 *  - Legacy UI array   : { type: 'fuel', fuelAmount, fuelType: 'JetA' | 'AvGas' }
 *
 * Returns null when there is no fuel service (or zero gallons) so the planner
 * dispatches a truck only for aircraft that actually requested fuel.
 */

export type FuelType = 'jet_a' | 'avgas'

export interface FuelRequest {
  gallons: number
  fuelType: FuelType
}

type ServiceEntry = { type: string; [k: string]: unknown }

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

/** True when a service entry denotes fuel (any of the historical type keys). */
function isFuelEntry(type: string): boolean {
  const t = type.toLowerCase()
  return t === 'fuel' || t === 'fuel_jeta' || t === 'fuel_100ll' || t.startsWith('fuel_')
}

/** Resolve the fuel type from an explicit field, else the type-key suffix. */
function resolveFuelType(entry: ServiceEntry): FuelType {
  const explicit = (entry.fuel ?? entry.fuelType) as unknown
  if (typeof explicit === 'string') {
    const e = explicit.toLowerCase()
    if (e.includes('avgas') || e.includes('100ll')) return 'avgas'
    if (e.includes('jet')) return 'jet_a'
  }
  const t = entry.type.toLowerCase()
  if (t.includes('100ll') || t.includes('avgas')) return 'avgas'
  return 'jet_a'
}

export function parseFuelRequest(services: ServiceEntry[] | undefined | null): FuelRequest | null {
  if (!Array.isArray(services)) return null
  for (const entry of services) {
    if (!entry || typeof entry.type !== 'string' || !isFuelEntry(entry.type)) continue
    const gallons = num(entry.gallons) ?? num(entry.quantity) ?? num(entry.fuelAmount)
    if (gallons == null || gallons <= 0) continue
    return { gallons, fuelType: resolveFuelType(entry) }
  }
  return null
}
